import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap'
import { OnityPolicyData } from '../../auth/core/_models'
import { useAuth } from '../../auth'
import { KTSVG } from '../../../../_metronic/helpers'

// IST Date helpers
const formatDateIST = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      const year = parts[0]
      const month = parts[1]
      const day = parts[2]
      return `${day}/${month}/${year}`
    }
    return dateString
  } catch {
    return ''
  }
}

const toISTInputFormat = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    const dateOnly = dateString.split('T')[0]
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly
    }
    const parts = dateOnly.split('-')
    if (parts.length >= 3) {
      return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
    }
    return ''
  } catch {
    return ''
  }
}

type Props = {
  policyData: OnityPolicyData
  policyEffectiveDate?: string
  policyExpiryDate?: string
  policyCrmDbName?: string
  onSuccess: () => void
  preloadedMembers?: EnrolledMember[]
  initialDependents?: any[]
}

interface EnrolledMember {
  Id: string
  EmployeeNumber: string
  Name: string
  Dob: string
  Relation: string
  Gender: string
  Premium: number
  Grade: string
  CrmClientId: string
  PremiumOpted: boolean | null
  AdditionalPremium: number | null
  LivesCharged: number | null
  GradeLower: string | null
  TopupName: string | null
  TopupSumInsured: number | null
  TopupBasePremium: number | null
  TopupGst: number | null
  TopupTotalPremium: number | null
  BasePremium: number | null
  BaseGst: number | null
  BaseTotalPremium: number | null
  TotalPremiumPayable: number | null
  TotalGst: number | null
  BaseSumInsured: number | null
  CreatedAt: string
}

type Dependent = {
  id: number
  name: string
  dob: string
  relation: string
  gender: string
}

type FlexiPlan = {
  name: string
  sumInsured: number
  premium: number
}

const RELATIONS = ['Spouse', 'Child']

const GENDERS = ['Male', 'Female', 'Other']

const GST_RATE = 0.18

// Grade-based sum insured mapping
const GRADE_SUM_INSURED: Record<string, number> = {
  '1': 500000,
  '2': 750000,
  '3': 1000000,
  'Grade 1': 500000,
  'Grade 2': 750000,
  'Grade 3': 1000000,
  'grade 1': 500000,
  'grade 2': 750000,
  'grade 3': 1000000,
}

const getGradeSumInsured = (grade: string | undefined): number => {
  if (!grade) return 500000
  // Try exact match first
  if (GRADE_SUM_INSURED[grade]) return GRADE_SUM_INSURED[grade]
  // Try extracting number
  const match = grade.match(/(\d+)/)
  if (match) {
    const num = match[1]
    if (GRADE_SUM_INSURED[num]) return GRADE_SUM_INSURED[num]
  }
  return 500000 // default
}

// Flexi Benefit Plans (Top-up)
const FLEXI_PLANS: FlexiPlan[] = [
  { name: 'Bronze', sumInsured: 300000, premium: 7300 },
  { name: 'Silver', sumInsured: 500000, premium: 9300 },
  { name: 'Gold', sumInsured: 700000, premium: 14500 },
  { name: 'Platinum', sumInsured: 1000000, premium: 40000 },
  { name: 'Platinum++', sumInsured: 1500000, premium: 50000 },
]

// Coverage comparison data
const COVERAGE_DATA = [
  {
    label: 'Sum Insured',
    values: ['INR 3,00,000+', 'INR 5,00,000+', 'INR 7,00,000+', 'INR 10,00,000+', 'INR 15,00,000+'],
  },
  {
    label: 'Maternity',
    values: ['10K+', '15K+', '20K+', '25K+', '30K+'],
  },
  {
    label: 'Infertility Treatment',
    values: ['10K+', '15K+', '20K+', '25K+', '30K+'],
  },
  {
    label: 'Room Rent',
    values: ['500+', 'INR 2,000+', 'INR 3,000+', 'INR 12,000+', 'INR 12,000+'],
  },
  {
    label: 'Cataract',
    values: ['1,000+', 'INR 3,000+', 'INR 7,000+', 'INR 10,000+', 'INR 15,000+'],
  },
  {
    label: 'ARMD (Age Related Macular Degeneration) - New Cover*',
    values: ['10,000', 'INR 40,000', 'INR 50,000', 'INR 60,000', 'INR 70,000'],
  },
  {
    label: 'Twins & Triplets Maternity Limit',
    values: ['10,000+', 'INR 35,000+', 'INR 45,000+', 'INR 55,000+', 'INR 65,000+'],
  },
  {
    label: 'Lasik Surgery',
    values: ['NA', 'NA', 'NA', '5+/- (Emp only)', '5+/- (Emp only)'],
  },
  {
    label: 'RCT',
    values: ['NA', 'INR 5,000+', 'INR 5,000+', 'INR 10,000+', 'INR 10,000+'],
  },
  {
    label: 'OPD Limit',
    values: ['2,000', '3,000', '5,000', 'INR 10,000', 'INR 10,000'],
  },
  {
    label: 'Siblings Cover',
    values: ['NA', 'NA', 'NA', 'Restricted to 1', 'Restricted to 1'],
  },
]

const PLAN_NAMES = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Platinum++']
const PLAN_PREMIUMS = ['₹7,300', '₹9,300', '₹14,500', '₹40,000', '₹50,000']

const getAgeAsOfDate = (dob: string, asOfDate: string) => {
  const birthDate = new Date(dob)
  const refDate = new Date(asOfDate)
  if (Number.isNaN(birthDate.getTime()) || Number.isNaN(refDate.getTime())) return 0

  let age = refDate.getFullYear() - birthDate.getFullYear()
  const birthdayThisYear = new Date(refDate.getFullYear(), birthDate.getMonth(), birthDate.getDate())

  if (refDate < birthdayThisYear) {
    age--
  } else if (refDate > birthdayThisYear) {
    age++
  }

  return age
}

export const AcsGlobalBaseEnrollmentForm: React.FC<Props> = ({
  policyData,
  policyEffectiveDate,
  policyExpiryDate,
  policyCrmDbName,
  onSuccess,
  preloadedMembers,
  initialDependents,
}) => {
  // Clients without flexi option
  const NO_FLEXI_CLIENTS: string[] = ['FIB0000873C']
  const flexiEnabled = !NO_FLEXI_CLIENTS.includes(policyCrmDbName || '')

  // Clients with 0% GST
  const ZERO_GST_CLIENTS: string[] = ['FIB0000866C', 'FIB0000867C']
  const gstRate = ZERO_GST_CLIENTS.includes(policyCrmDbName || '') ? 0 : GST_RATE

  // Per-life premium for dependents (Spouse + Children) — client-specific
  const PER_LIFE_PREMIUM_CLIENTS: Record<string, number> = {
    'FIB0000873C': 6992,
  }
  const perLifePremium = PER_LIFE_PREMIUM_CLIENTS[policyCrmDbName || ''] || 0

  // ─── Pro-rata helpers ───────────────────────────────────────────────
  const parseDateOnlyToUtcMs = (dateString: string | null | undefined): number | null => {
    if (!dateString) return null
    const dateOnly = dateString.split('T')[0]?.trim()
    if (!dateOnly) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      const [year, month, day] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateOnly)) {
      const [day, month, year] = dateOnly.split('/').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    const ymd = toISTInputFormat(dateOnly)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-').map((p) => Number(p))
      if ([year, month, day].some((n) => Number.isNaN(n))) return null
      return Date.UTC(year, month - 1, day)
    }
    return null
  }

  const daysBetweenInclusive = (fromMsUtc: number, toMsUtc: number): number => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000
    if (toMsUtc < fromMsUtc) return 0
    return Math.floor((toMsUtc - fromMsUtc) / MS_PER_DAY) + 1
  }

  const getProrationFactor = (): number => {
    // Resolve DOJ: prefer JoiningDate from initialDependents (API response), fallback to policyData.DOJ
    let dojRaw: any = undefined
    if (Array.isArray(initialDependents) && initialDependents.length > 0) {
      for (let i = 0; i < initialDependents.length; i++) {
        const row: any = initialDependents[i]
        if (row?.JoiningDate != null) { dojRaw = row.JoiningDate; break }
        if (row?.joiningdate != null) { dojRaw = row.joiningdate; break }
        if (row?.joiningDate != null) { dojRaw = row.joiningDate; break }
      }
    }
    if (dojRaw == null) dojRaw = policyData.DOJ

    const dojMs = parseDateOnlyToUtcMs(dojRaw)
    const effectiveMs = parseDateOnlyToUtcMs(policyEffectiveDate)
    const expiryMs = parseDateOnlyToUtcMs(policyExpiryDate)

    if (dojMs === null || effectiveMs === null || expiryMs === null) return 1
    // Only new joiners (DOJ strictly after policy start) are pro-rated
    if (dojMs <= effectiveMs) return 1

    const daysRemaining = daysBetweenInclusive(dojMs, expiryMs)
    const factor = daysRemaining / 365
    if (!Number.isFinite(factor)) return 1
    return Math.max(0, Math.min(1, factor))
  }

  const prorationFactor = getProrationFactor()
  const isProrated = prorationFactor < 1

  const [dependents, setDependents] = useState<Dependent[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDependentId, setEditingDependentId] = useState<number | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [selectedFlexi, setSelectedFlexi] = useState<FlexiPlan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [enrolledMembers, setEnrolledMembers] = useState<EnrolledMember[]>([])
  const [loadingFrozen, setLoadingFrozen] = useState(false)
  const { currentUser } = useAuth()

  const [newDependent, setNewDependent] = useState<Dependent>({
    id: 0,
    name: '',
    dob: '',
    relation: '',
    gender: '',
  })

  const baseSumInsured = getGradeSumInsured(policyData.Grade)

  // Employee + Spouse + 2 Children = max 3 dependents (excluding self)
  const maxTotalDependents = 3

  const getLatestDate = () => {
    const policyDate = policyEffectiveDate ? new Date(policyEffectiveDate) : null
    const dojDate = policyData.DOJ ? new Date(policyData.DOJ) : null

    if (policyDate && dojDate) {
      return policyDate > dojDate ? policyEffectiveDate! : policyData.DOJ
    }
    return policyDate ? policyEffectiveDate! : dojDate ? policyData.DOJ : new Date().toISOString()
  }

  const ageReferenceDate = getLatestDate()

  useEffect(() => {
    if (preloadedMembers && preloadedMembers.length > 0) {
      setEnrolledMembers(preloadedMembers)
      setLoadingFrozen(false)
    } else if (initialDependents && initialDependents.length > 0) {
      const mappedDependents = initialDependents
        .map((item, index) => {
          let relation = item.Relation
          const rLower = relation ? relation.toLowerCase() : ''

          if (['husband', 'wife', 'spouse'].includes(rLower)) relation = 'Spouse'
          else if (['son', 'daughter', 'child'].includes(rLower)) relation = 'Child'

          return {
            id: item.sid || Date.now() + index,
            name: item.Name,
            dob: item.DOB ? toISTInputFormat(item.DOB) : '',
            relation: relation,
            gender: item.Gender || '',
          }
        })
        .filter(
          (dep) => dep.relation === 'Self' || dep.relation === 'Spouse' || dep.relation === 'Child'
        )
      setDependents(mappedDependents)
    } else {
      setDependents([
        {
          id: 1,
          name: policyData.EmployeeName,
          dob: policyData.DOB ? toISTInputFormat(policyData.DOB) : '',
          relation: 'Self',
          gender: policyData.Gender || '',
        },
      ])
    }
  }, [policyData, preloadedMembers, initialDependents])

  const calculatePremium = () => {
    // Per-life premium for dependents if configured for this client
    const familyDeps = dependents.filter((d) => d.relation !== 'Self')
    const dependentCount = perLifePremium > 0 ? familyDeps.length : 0

    // Apply pro-rata to base per-life premium (new joiners only; factor = 1 for full-year members)
    const rawDependentPremium = dependentCount * perLifePremium
    const dependentPremium = Math.round(rawDependentPremium * prorationFactor)

    // Apply pro-rata to flexi/top-up premium
    const rawFlexiPremium = selectedFlexi ? selectedFlexi.premium : 0
    const flexiPremium = Math.round(rawFlexiPremium * prorationFactor)

    const dependentGST = Math.round(dependentPremium * gstRate)
    const dependentTotal = dependentPremium + dependentGST

    const flexiGST = Math.round(flexiPremium * gstRate)
    const flexiTotal = flexiPremium + flexiGST

    const totalTaxable = dependentPremium + flexiPremium
    const totalGST = dependentGST + flexiGST
    const totalPayable = totalTaxable + totalGST

    return {
      dependentPremium,
      dependentCount,
      dependentGST,
      dependentTotal,
      flexiPremium,
      flexiGST,
      flexiTotal,
      gst: totalGST,
      total: totalPayable,
    }
  }

  const handleAddClick = () => {
    setError(null)
    const familyDependents = dependents.filter((d) => d.relation !== 'Self')

    if (familyDependents.length >= maxTotalDependents) {
      setError(`Maximum of ${maxTotalDependents} dependents allowed (1 Spouse + 2 Children).`)
      return
    }

    setNewDependent({
      id: Date.now(),
      name: '',
      dob: '',
      relation: '',
      gender: '',
    })
    setShowAddModal(true)
  }

  const validateDependent = (dep: Dependent) => {
    setError(null)
    if (!dep.name || !dep.dob || !dep.relation || !dep.gender) {
      setError('All fields are required')
      return false
    }

    if (!RELATIONS.includes(dep.relation)) {
      setError('Only Spouse and Child relations are allowed.')
      return false
    }

    const age = getAgeAsOfDate(dep.dob, ageReferenceDate)

    // Age validations: Spouse 18-99, Child 0-25
    if (dep.relation === 'Spouse' && (age < 18 || age > 99)) {
      setError('Spouse must be between 18 and 99 years of age.')
      return false
    }

    if (dep.relation === 'Child' && age > 25) {
      setError('Child must be 25 years or below.')
      return false
    }

    // Exclude the currently-editing dependent from counts
    const otherDeps = editingDependentId
      ? dependents.filter((d) => d.id !== editingDependentId)
      : dependents

    // Relation count validations
    const relationCounts = otherDeps.reduce((acc, curr) => {
      acc[curr.relation] = (acc[curr.relation] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    if (dep.relation === 'Spouse' && relationCounts['Spouse']) {
      setError('Only 1 Spouse allowed.')
      return false
    }

    if (dep.relation === 'Child' && (relationCounts['Child'] || 0) >= 2) {
      setError('Maximum 2 Children allowed.')
      return false
    }

    return true
  }

  const saveDependent = () => {
    if (!validateDependent(newDependent)) return

    if (editingDependentId) {
      setDependents(dependents.map((d) => (d.id === editingDependentId ? newDependent : d)))
    } else {
      setDependents([...dependents, newDependent])
    }
    setEditingDependentId(null)
    setShowAddModal(false)
  }

  const handleEditClick = (dep: Dependent) => {
    setError(null)
    setEditingDependentId(dep.id)
    setNewDependent({ ...dep })
    setShowAddModal(true)
  }

  const removeDependent = (id: number) => {
    setDependents(dependents.filter((d) => d.id !== id))
    setError(null)
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    setError(null)
    setIsSubmitting(true)

    const premiumData = calculatePremium()

    const familyMembersWithPremium = dependents.map((dep) => {
      const baseMember = {
        EmployeeNumber: currentUser?.EmployeeId,
        name: dep.name,
        dob: dep.dob,
        relation: dep.relation,
        gender: dep.gender,
        premium: 0,
        Grade: policyData.Grade,
        crm_client_id: policyCrmDbName || policyData.CorpCode,
        Email: currentUser?.Email,
        CorporateCode: policyData.CorporateCode,
        SumInsured: baseSumInsured,
      }

      if (dep.relation === 'Self') {
        return {
          ...baseMember,
          premiumOpted: Boolean(selectedFlexi),
          additionalPremium: premiumData.total,
          livesCharged: premiumData.dependentCount,
          grade: policyData.Grade,

          // Flexi Benefit details (mapped as topup fields for same endpoint)
          TopupName: selectedFlexi?.name || '',
          topupsumInsured: selectedFlexi?.sumInsured || 0,
          topup_base_premium: premiumData.flexiPremium,
          topup_gst: premiumData.flexiGST,
          topup_total_premium: premiumData.flexiTotal,

          // Base (Dependent) details
          base_premium: premiumData.dependentPremium,
          base_gst: premiumData.dependentGST,
          base_total_premium: premiumData.dependentTotal,

          // Grand totals
          total_premium_payable: premiumData.total,
          total_gst: premiumData.gst,
        }
      }

      // For dependents, include pro-rated per-life premium if applicable
      const depPremium = Math.round(perLifePremium * prorationFactor)
      const depGst = Math.round(depPremium * gstRate)
      return {
        ...baseMember,
        premium: depPremium,
        base_premium: depPremium,
        base_gst: depGst,
        base_total_premium: depPremium + depGst,
      }
    })

    console.log('Submitting payload:', familyMembersWithPremium)

    try {
      const response = await fetch(
        'https://etl.futurisk.in/webhook/2dd87d62-8fd3-4cf6-a7e9-2ae88a23af4c',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(familyMembersWithPremium),
        }
      )

      if (response.ok) {
        setShowSuccess(true)
      } else {
        setError('Failed to submit enrollment. Please try again.')
        setIsSubmitting(false)
      }
    } catch (e) {
      console.error(e)
      setError('Network error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  const premiumInfo = calculatePremium()

  // ─── Frozen / Read-only State ───────────────────────────────────────
  if (policyData.freezeEnrolment) {
    if (loadingFrozen && (!preloadedMembers || preloadedMembers.length === 0))
      return (
        <div className='text-center p-5 text-primary'>
          <span className='spinner-border spinner-border-sm align-middle ms-2'></span> Loading
          enrollment details...
        </div>
      )

    const membersToDisplay =
      preloadedMembers && preloadedMembers.length > 0 ? preloadedMembers : enrolledMembers

    const frozenPremium = {
      dependentPremium: membersToDisplay.reduce((acc, m) => acc + (m.BasePremium || 0), 0),
      flexiPremium: membersToDisplay.reduce((acc, m) => acc + (m.TopupBasePremium || 0), 0),
      gst: membersToDisplay.reduce((acc, m) => acc + (m.TotalGst || 0), 0),
      total: membersToDisplay.reduce((acc, m) => acc + (m.TotalPremiumPayable || 0), 0),
    }

    const optedFlexiMember = membersToDisplay.find((m) => m.TopupName)
    const optedFlexi = optedFlexiMember?.TopupName
    const optedFlexiSI = optedFlexiMember?.TopupSumInsured

    return (
      <div className='card'>
        <div className='card-body'>
          <Row>
            <Col md={8}>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h3 className='mb-0'>Enrolled Family Details</h3>
                {baseSumInsured > 0 && (
                  <div className='badge badge-light-primary fs-6 p-3'>
                    Base Sum Insured: ₹{baseSumInsured.toLocaleString('en-IN')} (
                    {policyData.Grade})
                  </div>
                )}
              </div>
              <div className='table-responsive mb-5'>
                <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted'>
                      <th>Name</th>
                      <th>Relation</th>
                      <th>Gender</th>
                      <th>Date of Birth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersToDisplay.map((member) => (
                      <tr key={member.Id}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex justify-content-start flex-column'>
                              <div className='text-dark fw-bolder fs-6'>{member.Name}</div>
                            </div>
                          </div>
                        </td>
                        <td>{member.Relation}</td>
                        <td>{member.Gender}</td>
                        <td>{formatDateIST(member.Dob)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Selected Flexi Plan */}
              {flexiEnabled && <>
              <h3 className='mb-3'>Selected Flexi Benefits Plan</h3>
              <div className='p-4 bg-light rounded border border-gray-300'>
                {optedFlexi ? (
                  <div className='fw-bold fs-5 text-gray-800'>
                    {optedFlexi}{' '}
                    {optedFlexiSI
                      ? `- Sum Insured: ₹${optedFlexiSI.toLocaleString('en-IN')}`
                      : ''}
                  </div>
                ) : (
                  <div className='text-muted'>No Flexi Benefits plan selected.</div>
                )}
              </div>
              </>}
            </Col>
            <Col md={4}>
              <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
                <h4 className='mb-4'>Premium Summary</h4>

                {frozenPremium.dependentPremium > 0 && (
                  <div className='mb-3'>
                    <strong>Dependent Premium:</strong>
                    <div className='float-end'>
                      ₹{frozenPremium.dependentPremium.toLocaleString('en-IN')}
                    </div>
                  </div>
                )}

                {frozenPremium.flexiPremium > 0 && (
                  <div className='mb-3'>
                    <strong>Flexi Benefits Premium:</strong>
                    <div className='float-end'>
                      ₹{frozenPremium.flexiPremium.toLocaleString('en-IN')}
                    </div>
                  </div>
                )}

                {frozenPremium.total > 0 ? (
                  <>
                    <hr />
                    <div className='mb-2'>
                      <span>Total Amount:</span>
                      <span className='float-end'>
                        ₹{(frozenPremium.dependentPremium + frozenPremium.flexiPremium).toLocaleString('en-IN')}
                      </span>
                    </div>
                    {gstRate > 0 && (
                      <div className='mb-2'>
                        <span>GST ({(gstRate * 100).toFixed(0)}%):</span>
                        <span className='float-end'>₹{frozenPremium.gst.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <hr />
                    <h5 className='text-primary'>
                      Total Payable:
                      <span className='float-end'>₹{frozenPremium.total.toLocaleString('en-IN')}</span>
                    </h5>
                    <div className='text-muted fs-7 mt-2 text-end'>
                      The total premium will be deducted from your next salary.
                    </div>
                  </>
                ) : (
                  <div className='text-muted text-center py-5'>
                    No additional premium applicable.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    )
  }

  // ─── Success State ──────────────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className='card'>
        <div className='card-body text-center py-5'>
          <div className='mb-4'>
            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-3x svg-icon-success' />
          </div>
          <h2 className='text-success mb-4'>Thank You!</h2>

          {premiumInfo.total > 0 ? (
            <div className='alert alert-info d-inline-block p-4'>
              <span className='fs-4'>
                An amount of <strong>₹{premiumInfo.total.toLocaleString('en-IN')}</strong> will be
                deducted from your salary.
              </span>
            </div>
          ) : (
            <div className='fs-4 text-gray-600'>Your enrollment is complete.</div>
          )}
        </div>
      </div>
    )
  }

  // ─── Active Enrollment State ────────────────────────────────────────
  return (
    <div className='card'>
      <div className='card-body'>
        {error && !showAddModal && (
          <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>{error}</div>
        )}
        <Row>
          <Col md={8}>
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <div>
                <h3 className='mb-0'>Family Details</h3>
                {baseSumInsured > 0 && (
                  <div className='text-primary fw-bold mt-1'>
                    Base Sum Insured: ₹{baseSumInsured.toLocaleString('en-IN')} (
                    {policyData.Grade})
                  </div>
                )}
                <div className='text-muted fs-7 mt-1'>
                  Coverage: Employee + Spouse + 2 Children
                </div>
              </div>
              <Button
                variant='primary'
                onClick={handleAddClick}
                disabled={
                  dependents.filter((d) => d.relation !== 'Self').length >= maxTotalDependents
                }
              >
                Add Dependent
              </Button>
            </div>

            <div className='table-responsive mb-5'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bolder'>
                    <th className='min-w-150px'>Name</th>
                    <th className='min-w-100px'>Relation</th>
                    <th className='min-w-100px'>Gender</th>
                    <th className='min-w-100px'>DOB</th>
                    <th className='min-w-100px'>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dependents.map((dep) => (
                    <tr key={dep.id} className='text-capitalize text-dark'>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='fw-bolder text-hover-primary fs-6 text-dark'>
                              {dep.name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.relation}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {dep.gender}
                        </span>
                      </td>
                      <td>
                        <span className='fw-bolder text-hover-primary d-block fs-6 text-dark'>
                          {formatDateIST(dep.dob)}
                        </span>
                      </td>
                      <td>
                        {dep.relation !== 'Self' && (
                          <div className='d-flex gap-2'>
                            <Button
                              variant='primary'
                              size='sm'
                              onClick={() => handleEditClick(dep)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant='danger'
                              size='sm'
                              onClick={() => removeDependent(dep.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ─── Flexi Benefits Section ─────────────────────────── */}
            {flexiEnabled && <div className='mb-5'>
              <h3 className='mb-3'>
                <KTSVG path='/media/icons/duotune/general/gen049.svg' className='svg-icon-2 svg-icon-warning me-2' />
                Flexi Benefits Plans
              </h3>

              <div className='alert alert-info d-flex align-items-center mb-4'>
                <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-info me-3' />
                <div className='text-gray-700 fw-bold fs-7'>
                  Flexi Benefits provide additional sum insured with enhanced coverage over and
                  above your base policy. Choose one plan that best suits your family's needs. The
                  premium {gstRate > 0 ? '(excluding GST@' + (gstRate * 100).toFixed(0) + '%)' : ''} will be deducted from your salary.
                </div>
              </div>

              {/* Coverage Comparison Table */}
              <div className='table-responsive mb-4'>
                <table className='table table-bordered table-hover table-sm align-middle'>
                  <thead>
                    <tr className='bg-primary text-white text-center'>
                      <th
                        className='text-start ps-3 align-middle'
                        style={{ minWidth: '180px', position: 'sticky', left: 0, background: '#009ef7', zIndex: 1 }}
                      >
                        Coverage
                      </th>
                      {PLAN_NAMES.map((plan, idx) => (
                        <th
                          key={idx}
                          className={`align-middle ${
                            selectedFlexi?.name === plan
                              ? 'bg-success text-white'
                              : ''
                          }`}
                          style={{ minWidth: '120px', cursor: 'pointer' }}
                          onClick={() => {
                            const fp = FLEXI_PLANS.find((f) => f.name === plan)
                            if (fp) setSelectedFlexi(selectedFlexi?.name === plan ? null : fp)
                          }}
                        >
                          <div className='fw-bolder fs-7'>{plan}</div>
                          {selectedFlexi?.name === plan && (
                            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-2 svg-icon-white' />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COVERAGE_DATA.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td
                          className='fw-bold text-gray-800 ps-3 fs-8'
                          style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}
                        >
                          {row.label}
                        </td>
                        {row.values.map((val, colIdx) => (
                          <td
                            key={colIdx}
                            className={`text-center fs-8 ${
                              selectedFlexi?.name === PLAN_NAMES[colIdx]
                                ? 'bg-light-success fw-bold'
                                : ''
                            } ${val === 'NA' ? 'text-muted' : 'text-dark'}`}
                          >
                            {val}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Premium Row */}
                    <tr className='bg-light-warning'>
                      <td
                        className='fw-bolder text-gray-900 ps-3 fs-7'
                        style={{ position: 'sticky', left: 0, background: '#fff3cd', zIndex: 1 }}
                      >
                        FLEXI + OPD Premium
                        <br />
                        <small className='text-muted'>(Per Family{gstRate > 0 ? ', Excl. GST@' + (gstRate * 100).toFixed(0) + '%' : ''})</small>
                      </td>
                      {PLAN_PREMIUMS.map((price, idx) => (
                        <td
                          key={idx}
                          className={`text-center fw-bolder fs-7 ${
                            selectedFlexi?.name === PLAN_NAMES[idx]
                              ? 'bg-success text-white'
                              : 'text-primary'
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const fp = FLEXI_PLANS[idx]
                            setSelectedFlexi(selectedFlexi?.name === fp.name ? null : fp)
                          }}
                        >
                          {price}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Radio Selection */}
              <div className='border rounded p-4 bg-light-primary'>
                <div className='fw-bold fs-6 mb-3 text-gray-800'>Select Your Plan:</div>
                <Form.Group>
                  <Form.Check
                    type='radio'
                    label={
                      <span className='fw-bold text-gray-700'>
                        No Flexi Benefits
                      </span>
                    }
                    name='flexiPlan'
                    id='flexi-none'
                    checked={selectedFlexi === null}
                    onChange={() => setSelectedFlexi(null)}
                    className='mb-3 p-2 ps-6 rounded border bg-white'
                  />
                  {FLEXI_PLANS.map((plan, idx) => (
                    <Form.Check
                      key={idx}
                      type='radio'
                      label={
                        <span className='fw-bold'>
                          <span className={`badge ${selectedFlexi?.name === plan.name ? 'badge-success' : 'badge-light-primary'} me-2`}>
                            {plan.name}
                          </span>
                          Sum Insured: ₹{plan.sumInsured.toLocaleString('en-IN')} &mdash; Premium: ₹
                          {plan.premium.toLocaleString('en-IN')}
                          {gstRate > 0 && <span className='text-muted fs-8'> + GST</span>}
                        </span>
                      }
                      name='flexiPlan'
                      id={`flexi-${idx}`}
                      checked={selectedFlexi?.name === plan.name}
                      onChange={() => setSelectedFlexi(plan)}
                      className={`mb-2 p-2 ps-6 rounded border ${
                        selectedFlexi?.name === plan.name
                          ? 'bg-light-success border-success'
                          : 'bg-white'
                      }`}
                    />
                  ))}
                </Form.Group>
              </div>
            </div>}
          </Col>

          {/* ─── Premium Summary Sidebar ─────────────────────────── */}
          <Col md={4}>
            <div className='card bg-light p-4 sticky-top' style={{ top: '20px' }}>
              <h4 className='mb-4'>Premium Summary</h4>

              <div className='mb-3 p-3 bg-white rounded border'>
                <div className='text-muted fs-7'>Base Sum Insured ({policyData.Grade})</div>
                <div className='fw-bolder fs-4 text-primary'>
                  ₹{baseSumInsured.toLocaleString('en-IN')}
                </div>
                {perLifePremium > 0 ? (
                  <div className='text-muted fs-8 mt-1'>
                    ₹{perLifePremium.toLocaleString('en-IN')} per life (excl. GST) for dependents
                  </div>
                ) : (
                  <div className='text-muted fs-8 mt-1'>No premium for dependents</div>
                )}
              </div>

              {premiumInfo.dependentPremium > 0 && (
                <div className='mb-3 d-flex justify-content-between'>
                  <strong>Dependent Premium ({premiumInfo.dependentCount} {premiumInfo.dependentCount === 1 ? 'life' : 'lives'}):</strong>
                  <div className='float-end'>₹{premiumInfo.dependentPremium.toLocaleString('en-IN')}</div>
                </div>
              )}

              {flexiEnabled && selectedFlexi && (
                <div className='mb-3 d-flex justify-content-between'>
                  <strong>Flexi Benefits ({selectedFlexi.name}):</strong>
                  <div className='float-end'>₹{premiumInfo.flexiPremium.toLocaleString('en-IN')}</div>
                </div>
              )}

              {isProrated && (premiumInfo.dependentPremium > 0 || (flexiEnabled && selectedFlexi)) && (
                <div className='alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2'>
                  <span className='badge badge-warning me-2'>Pro-rated</span>
                  <span className='text-dark fs-7'>
                    Premium calculated for <strong>{Math.round(prorationFactor * 365)} days</strong> remaining in the policy year (factor: {(prorationFactor * 100).toFixed(1)}%).
                  </span>
                </div>
              )}

              {(premiumInfo.dependentPremium > 0 || (flexiEnabled && selectedFlexi)) ? (
                <>
                  <hr />
                  <div className='mb-2'>
                    <span>Total Amount:</span>
                    <span className='float-end'>
                      ₹{(premiumInfo.dependentPremium + premiumInfo.flexiPremium).toLocaleString('en-IN')}
                    </span>
                  </div>
                  {gstRate > 0 && (
                    <div className='mb-2'>
                      <span>GST ({(gstRate * 100).toFixed(0)}%):</span>
                      <span className='float-end'>₹{premiumInfo.gst.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <hr />
                  <h5 className='text-primary'>
                    Total Payable:
                    <span className='float-end'>₹{premiumInfo.total.toLocaleString('en-IN')}</span>
                  </h5>
                  <div className='text-muted fs-7 mt-2 text-end'>
                    This amount will be deducted from your salary.
                  </div>
                </>
              ) : (
                <div className='text-muted text-center py-5'>
                  No additional premium applicable.
                  {flexiEnabled && <><br />
                  <small>Select a Flexi Benefits plan to see premium.</small></>}
                </div>
              )}

              <div className='d-grid gap-2 mt-4'>
                <Button
                  variant='success'
                  size='lg'
                  onClick={() => setShowConfirmModal(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Enrollment'
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Add Dependent Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false)
          setEditingDependentId(null)
          setError(null)
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingDependentId ? 'Edit Dependent' : 'Add Dependent'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className='mb-4 text-danger fw-bold border border-danger p-3 rounded'>
              {error}
            </div>
          )}
          <div className='alert alert-light-info border border-info p-3 mb-4'>
            <div className='fs-8 text-gray-700'>
              <strong>Age Limits:</strong> Spouse: 18–99 years | Children: 0–25 years
            </div>
          </div>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='text'
                value={newDependent.name}
                onChange={(e) => setNewDependent({ ...newDependent, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Relation</Form.Label>
              <Form.Select
                value={newDependent.relation}
                onChange={(e) => setNewDependent({ ...newDependent, relation: e.target.value })}
              >
                <option value=''>Select Relation</option>
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Gender</Form.Label>
              <Form.Select
                value={newDependent.gender}
                onChange={(e) => setNewDependent({ ...newDependent, gender: e.target.value })}
              >
                <option value=''>Select Gender</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type='date'
                value={newDependent.dob}
                onChange={(e) => setNewDependent({ ...newDependent, dob: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => { setShowAddModal(false); setEditingDependentId(null) }}>
            Cancel
          </Button>
          <Button variant='primary' onClick={saveDependent}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Final Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        backdrop='static'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-warning d-flex align-items-center'>
            <span className='svg-icon svg-icon-2hx svg-icon-warning me-4'>
              <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x svg-icon-warning' />
            </span>
            <div className='d-flex flex-column'>
              {premiumInfo.total > 0 && (
                <>
                  <div className='fw-bold mb-1'>
                    Total Premium Deduction: ₹{premiumInfo.total.toLocaleString('en-IN')}
                  </div>
                  <div className='small'>This amount will be debited from your salary.</div>
                </>
              )}
              <div className='small mt-1'>
                <strong>
                  Please review and confirm your nominations carefully. Once submitted, no changes
                  can be made in the policy year.
                </strong>
              </div>
            </div>
          </div>
          <p className='mb-0 mt-3'>Do you want to proceed to enroll your family?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowConfirmModal(false)}>
            Go Back
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setShowConfirmModal(false)
              handleSubmit()
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Submitting...
              </>
            ) : (
              'Yes, Enroll My Family'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
